<template>
    <div class="grid-container">
        <header class="header">
            <div class="header-actions">
                <router-link to="/" class="logo-link">
                    <img src="@/assets/device-tracker-label-icon-v2.png" alt="Nordeus" class="logo"/>
                </router-link>
            </div>

            <div class="header-actions">
                <!--        <button @click="seedData" class="seed-btn">Seed Test Data</button>-->
                <LoginButton v-if="!user" @login-success="handleLoginSuccess"/>
                <div v-if="user" class="user-info">
                    <router-link to="/profile" class="profile-link">
                        <span class="profile-icon">👤</span>
                        {{ user.email }}
                    </router-link>
                    <button @click="logout" class="logout-btn">Logout</button>
                </div>
            </div>
        </header>

        <main class="main-content">
            <div class="controls-bar">
                <div class="search-filter-group">
                    <SearchBar :search-value="searchQuery" @search="handleSearch"/>
                    <FilterBar
                        :selected-filter="selectedFilter"
                        @filter-change="changeFilter"
                    />
                </div>
                <router-link v-if="userRole === 'admin'" to="/create-device" class="create-device-btn">
                    + Create Device
                </router-link>
            </div>

            <div class="devices-grid">
                <DeviceCardSkeleton v-if="loading" v-for="n in 14" :key="n"/>
                <DeviceCard v-else v-for="device in devices" :key="device.id" :device="device"/>
            </div>
        </main>

        <Pagination
            v-if="!loading && totalPages > 1"
            :current-page="currentPage"
            :total-pages="totalPages"
            @page-change="changePage"
        />

        <AppFooter/>
    </div>
</template>

<script setup>
import {ref, computed, onMounted, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {auth, provider} from "@/firebase";
import {signInWithPopup, signOut, onAuthStateChanged} from "firebase/auth";
import {collection, getDocs} from "firebase/firestore";
import {db} from "@/firebase";
import {getUserRole} from "../services/userService";
import {COLORS, FONTS, GRADIENTS} from "@/styles/brand";
import DeviceCard from "./DeviceCard.vue";
import DeviceCardSkeleton from "./DeviceCardSkeleton.vue";
import LoginButton from "./LoginButton.vue";
import AppFooter from "./AppFooter.vue";
import Pagination from "./Pagination.vue";
import FilterBar from "./FilterBar.vue";
import SearchBar from "./SearchBar.vue";

const route = useRoute();
const router = useRouter();

const user = ref(null);
const userRole = ref('user');
const allDevices = ref([]);
const filteredDevices = ref([]);
const devices = ref([]);
const loading = ref(true);
const currentPage = ref(1);
const itemsPerPage = 14;
const selectedFilter = ref({type: "all", os: "all", ram: "all", team: "all", owner: "all", status: "all"});
const searchQuery = ref("");

const handleLoginSuccess = (loggedInUser) => {
    user.value = loggedInUser;
};

const logout = async () => {
    await signOut(auth);
    user.value = null;
    router.push('/login');
};

const loadDevices = async () => {
    loading.value = true;
    const querySnapshot = await getDocs(collection(db, "devices"));
    const baseDevices = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
    }));

    // Multiply devices by 5 for testing TODO: remove this later
    allDevices.value = [];
    baseDevices.forEach((device, index) => {
        allDevices.value.push({
            ...device,
            id: `${device.id}`,
        });
    });


    applyFilter(false);
    loading.value = false;
};

const updatePaginatedDevices = () => {
    const start = (currentPage.value - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    devices.value = filteredDevices.value.slice(start, end);
};

const applyFilter = (resetPage = true) => {
    let filtered = allDevices.value;

    // Apply filters
    if (typeof selectedFilter.value === 'object') {
        const filters = selectedFilter.value;

        // Type filter
        if (filters.type !== 'all') {
            filtered = filtered.filter(device => device.type === filters.type);
        }

        // OS filter
        if (filters.os !== 'all') {
            filtered = filtered.filter(device => device.os === filters.os);
        }

        // RAM filter
        if (filters.ram !== 'all') {
            filtered = filtered.filter(device => {
                const ram = parseInt(device.ram) || 0;
                switch (filters.ram) {
                    case '<2GB': return ram < 2;
                    case '2-4GB': return ram >= 2 && ram < 4;
                    case '4-8GB': return ram >= 4 && ram < 8;
                    case '>8GB': return ram >= 8;
                    default: return true;
                }
            });
        }

        // Team filter
        if (filters.team !== 'all') {
            filtered = filtered.filter(device => device.team === filters.team);
        }

        // Owner filter
        if (filters.owner !== 'all') {
            filtered = filtered.filter(device => device.owner === filters.owner);
        }

        // Status filter
        if (filters.status !== 'all') {
            if (filters.status === 'available') {
                filtered = filtered.filter(device => !device.lastTakenBy || device.lastTakenBy === '');
            } else if (filters.status === 'taken') {
                filtered = filtered.filter(device => device.lastTakenBy && device.lastTakenBy !== '');
            }
        }
    } else if (selectedFilter.value !== "all") {
        // Legacy string filter support
        filtered = filtered.filter(device => device.type === selectedFilter.value);
    }

    // Apply search filter
    if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase().trim();
        filtered = filtered.filter(device =>
            device.model?.toLowerCase().includes(query) ||
            device.id?.toLowerCase().includes(query) ||
            device.modelId?.toLowerCase().includes(query),
        );
    }

    filteredDevices.value = filtered;
    if (resetPage) {
        currentPage.value = 1;
    }
    updatePaginatedDevices();
};

const updateURL = () => {
    const query = {};

    if (searchQuery.value.trim()) {
        query.search = searchQuery.value.trim();
    }

    if (typeof selectedFilter.value === 'object') {
        const filters = selectedFilter.value;
        if (filters.type !== 'all') query.type = filters.type;
        if (filters.os !== 'all') query.os = filters.os;
        if (filters.ram !== 'all') query.ram = filters.ram;
        if (filters.team !== 'all') query.team = filters.team;
        if (filters.owner !== 'all') query.owner = filters.owner;
        if (filters.status !== 'all') query.status = filters.status;
    } else if (selectedFilter.value !== 'all') {
        query.type = selectedFilter.value;
    }

    if (currentPage.value > 1) {
        query.page = currentPage.value.toString();
    }

    router.replace({ query });
};

const changeFilter = (filterObj) => {
    if (typeof filterObj === 'string') {
        selectedFilter.value = filterObj;
    } else {
        selectedFilter.value = filterObj;
    }
    applyFilter();
    updateURL();
};

const handleSearch = (query) => {
    searchQuery.value = query;
    applyFilter();
    updateURL();
};

const totalPages = computed(() => Math.ceil(filteredDevices.value.length / itemsPerPage));

const changePage = (page) => {
    currentPage.value = page;
    updatePaginatedDevices();
    updateURL();
};

const seedData = async () => {
    await seedAll();
    await loadDevices();
};

const loadFromURL = () => {
    const query = route.query;

    if (query.search) {
        searchQuery.value = query.search;
    }

    if (query.type || query.os || query.ram || query.team || query.owner || query.status) {
        selectedFilter.value = {
            type: query.type || 'all',
            os: query.os || 'all',
            ram: query.ram || 'all',
            team: query.team || 'all',
            owner: query.owner || 'all',
            status: query.status || 'all'
        };
    }

    if (query.page) {
        currentPage.value = parseInt(query.page) || 1;
    }
};

onMounted(() => {
    loadFromURL();
    loadDevices();
    onAuthStateChanged(auth, async (currentUser) => {
        if (currentUser && currentUser.email.endsWith("@nordeus.com")) {
            user.value = currentUser;
            userRole.value = await getUserRole(currentUser.email);
        } else {
            user.value = null;
            userRole.value = 'user';
        }
    });
});
</script>

<style scoped>
.grid-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(20, 64, 154, 0.8) 50%, rgba(31, 31, 79, 0.9) 100%),
    url('@/assets/nordeus-3840x1080-pattern_new.jpg') no-repeat center center fixed;
    background-size: cover;
    position: relative;
    font-family: v-bind('FONTS.GOOGLE');
}

.grid-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="0.5" fill="%23ffffff" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>') repeat;
    pointer-events: none;
}

.header {
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.8) 0%, rgba(20, 64, 154, 0.8) 50%, rgba(31, 31, 79, 0.9) 100%);
    border-bottom: 1px solid #000000;
    padding: 24px 32px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.logo-link {
    text-decoration: none;
}

.logo {
    height: 40px;
    cursor: pointer;
    transition: transform 0.3s ease;
}

.logo:hover {
    transform: scale(1.05);
}

.header-actions {
    display: flex;
    gap: 16px;
    align-items: center;
}

.header-actions h1 {
    color: v-bind('COLORS.PRIMARY.WHITE');
    font-family: v-bind('FONTS.PRIMARY');
    font-size: 2rem;
    font-weight: 700;
    margin: 0;
}

.logout-btn, .seed-btn {
    padding: 12px 24px;
    background: v-bind('COLORS.PRIMARY.RED');
    color: v-bind('COLORS.PRIMARY.WHITE');
    border: 2px solid v-bind('COLORS.PRIMARY.WHITE');
    border-radius: 50px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 8px;
}

.logout-btn:hover, .seed-btn:hover {
    transform: translateY(-2px) scale(1.05);
    background: v-bind('COLORS.PRIMARY.WHITE');
    color: v-bind('COLORS.PRIMARY.RED');
    border-color: v-bind('COLORS.PRIMARY.RED');
}

.user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    color: v-bind('COLORS.PRIMARY.RED');
    font-weight: 500;
}

.profile-link {
    color: v-bind('COLORS.PRIMARY.RED');
    text-decoration: none;
    font-weight: 500;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
}

.profile-link:hover {
    color: v-bind('COLORS.PRIMARY.WHITE');
    text-decoration: underline;
}

.profile-icon {
    font-size: 1.2rem;
}

.main-content {
    flex: 1;
    display: flex;
    flex-direction: column;
}


.create-device-btn {
    padding: 8px 12px;
    background: v-bind('COLORS.SECONDARY.GREEN + "95"');
    color: white;
    text-decoration: none;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.3s ease;
    white-space: nowrap;
    height: fit-content;
}

.create-device-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.search-filter-group {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;
}

.controls-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 32px 10px;
    gap: 20px;
    position: relative;
    z-index: 1;
}

.devices-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 24px;
    padding: 10px 32px 32px;
    position: relative;
    z-index: 1;
}


</style>
